import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowRightFill from '@/assets/icons/arrow_right_fill.svg?component';
import Checkbox from '@/components/atoms/Checkbox';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type { DataTableColumnDef } from '@/components/organisms/DataTable';
import { translateDataLabel } from '@/i18n/dataLabels';

import { CageActions, ExpandToggle, LocationActions, LocationSpecies } from './locationsTableCells';
import type { LocationsTableHandlers, LocationsTableRow } from './locationsTableTypes';

import styles from './LocationsTable.module.scss';

/**
 * The columns of the locations table. A pen row leaves the location's own fields blank:
 * timezone, site number and location type belong to the location, so one blank cell spans
 * them; species does not, and lines up under its own header.
 */
export function useLocationsTableColumns(
    handlers: LocationsTableHandlers,
    selectAll: ReactNode
): DataTableColumnDef<LocationsTableRow>[] {
    const { t } = useTranslation();

    return [
        {
            id: 'select',
            header: () => selectAll,
            cell: ({ row: { original } }) =>
                original.kind === 'location' ? (
                    <Checkbox
                        aria-label={t('clientLocations.locationsTable.selectLocation', {
                            name: original.location.name,
                        })}
                        checked={handlers.isSelected(original.location.id)}
                        onCheckedChange={() => handlers.onToggleSelected(original.location.id)}
                    />
                ) : null,
        },
        {
            id: 'expand',
            header: '',
            meta: { headerScreenReaderText: t('clientLocations.locationsTable.pensColumn') },
            cell: ({ row: { original } }) =>
                original.kind === 'location' ? (
                    <ExpandToggle location={original.location} handlers={handlers} />
                ) : (
                    <SvgIcon
                        icon={ArrowRightFill}
                        size="inherit"
                        className={styles.penArrow}
                        svgProps={{ 'aria-hidden': true }}
                    />
                ),
        },
        {
            id: 'name',
            header: t('common.labels.name'),
            cell: ({ row: { original } }) =>
                original.kind === 'location' ? original.location.name : original.cage.name,
        },
        {
            id: 'timezone',
            header: t('internal.clientLocations.columns.timezone'),
            meta: { colSpan: ({ original }) => (original.kind === 'cage' ? 3 : undefined) },
            cell: ({ row: { original } }) =>
                original.kind === 'location' ? original.location.timezone : null,
        },
        {
            id: 'siteNumber',
            header: t('internal.clientLocations.columns.siteNumber'),
            cell: ({ row: { original } }) =>
                original.kind === 'location' ? original.location.aquacultureSiteId : null,
        },
        {
            id: 'locationType',
            header: t('internal.clientLocations.columns.locationType'),
            cell: ({ row: { original } }) =>
                original.kind === 'location'
                    ? translateDataLabel(t, 'locationTypes', original.location.location_type.name)
                    : null,
        },
        {
            id: 'fishType',
            header: t('internal.clientLocations.columns.fishType'),
            cell: ({ row: { original } }) =>
                original.kind === 'location' ? (
                    <LocationSpecies location={original.location} />
                ) : original.cage.fishTypes ? (
                    translateDataLabel(t, 'fishTypes', original.cage.fishTypes.name)
                ) : (
                    '-'
                ),
        },
        {
            id: 'historical',
            header: t('common.labels.historical'),
            cell: ({ row: { original } }) => (original.kind === 'location' ? '-' : null),
        },
        {
            id: 'actions',
            header: t('common.labels.actions'),
            cell: ({ row: { original } }) =>
                original.kind === 'location' ? (
                    <LocationActions location={original.location} handlers={handlers} />
                ) : (
                    <CageActions
                        cage={original.cage}
                        location={original.location}
                        handlers={handlers}
                    />
                ),
        },
    ];
}
